import type {ViewItem} from 'app_api/projects/presence/types';
import {ChartType} from 'common/report/constants';
import {ContentsMapById, ContentType, LayoutResponse, MediaContent, TemplateType} from 'common/report/dashboards/types';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';

export interface SearchQueryFilter<T> {
  value: T;
  negated?: boolean;
}

export interface SearchQuerySort {
  key: SearchSortKey;
  order: SearchSortOrder;
}

export interface SearchQueryFilters {
  email: Array<SearchQueryFilter<string>>;
  type: Array<SearchQueryFilter<SearchDisplayContext>>;
}

export type TableSortKey = SearchSortKey | TableField;

export enum TableField {
  UniqueViews = `unique_count`,
  LastModified = `lastModified`,
  Name = `name`,
  User = `user`,
  Default = ``,
}

export interface TableSort {
  key: TableSortKey;
  order: SearchSortOrder;
}

export type MediaThumbnailInfo = Pick<MediaContent, 'media_type' | 'path'>;
export type ReportThumbnailInfo = {
  chart_type: ChartType;
  bookmark_type: BookmarkType;
};

export type DashboardThumbnailContents = {
  [ContentType.Media]?: ContentsMapById<MediaThumbnailInfo>;
  [ContentType.Report]?: ContentsMapById<ReportThumbnailInfo>;
  [ContentType.ReportLink]?: ContentsMapById<ReportThumbnailInfo>;
  [ContentType.Text]?: ContentsMapById<null>;
  [ContentType.Dashboard]?: ContentsMapById<null>;
};

export interface SearchResult {
  chart_type?: ChartType | null;
  created: string /** i.e. "2020-05-21T20:09:28" */;
  dashboard?: string;
  description?: string;
  email: string;
  thumbnail_layout?: LayoutResponse;
  thumbnail_contents?: DashboardThumbnailContents;
  id: number;
  last_edited: string /** i.e. "2020-05-21T20:09:28" */;
  last_modified_by_email?: string;
  last_modified_by_id?: number;
  last_modified_by_name?: string;
  last_viewed_by_user?: string /** i.e. "2020-05-21T20:09:28" */;
  link: string;
  name: string;
  presence_views?: ViewItem[];
  report?: string /** bookmark type or `dashboard` */;
  scores?: SearchResultScore;
  template_type?: TemplateType;
  total_count?: number;
  type: SearchResultType;
  unique_count: number /** number of users who viewed the entity */;
  user_id: number;
  user: string /** name of the user */;
}

export interface SearchResultScore {
  time?: number;
  filter?: number;
  equality?: number;
  text?: number;
  /** the higher the number of unique views, the higher the score */
  popularity: number;
  /** score based on text match, date modified, type filter, and algolia rank */
  final: number;
}

export enum SearchResultType {
  Campaign = `ENTITY_TYPE_CAMPAIGN`,
  Cohort = `ENTITY_TYPE_COHORT`,
  Dashboard = `ENTITY_TYPE_DASHBOARD`,
  Event = `ENTITY_TYPE_EVENT`,
  Help = `ENTITY_TYPE_HELP`,
  Message = `ENTITY_TYPE_MESSAGE`,
  Report = `ENTITY_TYPE_REPORT`,
}

export enum SearchDisplayContext {
  Dashboard = `dashboard`,
  Flows = `flows`,
  Funnels = `funnels`,
  Impact = `launch-analysis`,
  Insights = `insights`,
  Retention = `retention`,
}

export enum SearchDisplayContextDisplayName {
  Dashboard = `Boards`,
  Flows = `Flows`,
  Funnels = `Funnels`,
  Impact = `Impact`,
  Insights = `Insights`,
  Retention = `Retention`,
}

export enum SearchDisplayContextIcons {
  Dashboard = `brand-dashboard`,
  Flows = `brand-flows`,
  Funnels = `brand-funnels`,
  Impact = `brand-impact`,
  Insights = `brand-insights`,
  Retention = `brand-retention`,
}
export interface RecentSearchItem {
  /** icon of the search item, could be search or brand icons */
  icon?: string;
  /** unique key as RecentSearchItemType-name to be taken out*/
  key: string;
  /** label of the search item, like in items-menu */
  label: string;
  /** type of the search item */
  type: RecentSearchItemType;
  /** owner of the entity */
  user?: string;
  userEmail?: string;
  value?: string;

  // extra things for entities
  /** count of the entity */
  count?: number;
  /** description of the entity if applicable */
  description?: string;
  /** TODO remove this later, old link to the specific entity */
  link?: string;
  /** link to the specific entity */
  href?: string;
  /** sublabel of the search item */
  subLabel?: string;
  /** whether the current owner is the owner of the entity */
  isOwner?: boolean;
  /** report type based on its display context */
  report?: SearchDisplayContext;
  /** report id */
  id?: number;
}

export enum RecentSearchItemType {
  Entity = `entity`,
  User = `user`,
  SearchTerm = `searchTerm`,
}

// keep this consistent with app_api/projects/search
export enum SearchSortKey {
  LastModified = `last_edited`,
  Name = `name`,
  Popularity = `popularity`,
  RecentlyViewed = `recently_viewed`,
}

// keep this consistent with app_api/projects/search
export enum SearchSortOrder {
  Ascending = `ASC`,
  Descending = `DESC`,
}

// set sort via this enum, translates to above enums for API
export enum SearchSortOption {
  Relevance = `Relevance`,
  Popularity = `Most Viewed`,
  RecentlyViewed = `Recently Viewed`,
  LastModified = `Recently Modified`,
  TitleAscending = `Title A-Z`,
  TitleDescending = `Title Z-A`,
}
